import { 
  HealthStatus,
  ChainState,
  BridgeTransaction,
  TransactionFilter,
  AuditAlert,
  AlertFilter,
  ValidatorData,
  ValidatorHealthSummary,
  DashboardStats,
  AuditReport,
  ReconciliationResult,
  PaginatedResponse,
  TransactionVolumeData,
  LoginRequest,
  LoginResponse,
  User,
  CreateUserRequest,
  CreateUserResponse
} from '../types/api';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:3001';
const API_KEY = (import.meta as any).env?.VITE_API_KEY || '';

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    // Load token from localStorage if available
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Add authentication header
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else if (API_KEY) {
      headers['x-api-key'] = API_KEY;
    }

    const response = await fetch(url, {
      headers,
      ...options,
    });

    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        // Clear invalid token
        this.setToken(null);
        throw new Error('Authentication failed. Please login again.');
      }
      
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
    }

    return response.json();
  }

  // Health and Status
  async getHealth(): Promise<HealthStatus> {
    return this.request<HealthStatus>('/health');
  }

  async getChainStatus(): Promise<ChainState[]> {
    return this.request<ChainState[]>('/api/status/chains');
  }

  async getDashboardStats(): Promise<DashboardStats> {
    return this.request<DashboardStats>('/api/status/system');
  }

  // Transactions
  async getTransactions(filter: TransactionFilter = {}, page = 1, limit = 20): Promise<PaginatedResponse<BridgeTransaction>> {
    const params = new URLSearchParams();
    
    if (filter.status?.length) params.append('status', filter.status.join(','));
    if (filter.riskLevel?.length) params.append('riskLevel', filter.riskLevel.join(','));
    if (filter.chains?.length) params.append('chains', filter.chains.join(','));
    if (filter.dateRange?.start) params.append('startTime', filter.dateRange.start.toISOString());
    if (filter.dateRange?.end) params.append('endTime', filter.dateRange.end.toISOString());
    if (filter.minAmount) params.append('minAmount', filter.minAmount);
    if (filter.maxAmount) params.append('maxAmount', filter.maxAmount);
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const queryString = params.toString();
    return this.request<PaginatedResponse<BridgeTransaction>>(`/api/transactions${queryString ? `?${queryString}` : ''}`);
  }

  async getTransaction(txHash: string): Promise<BridgeTransaction> {
    return this.request<BridgeTransaction>(`/api/transactions/${txHash}`);
  }

  // Alerts
  async getAlerts(filter: AlertFilter = {}, page = 1, limit = 20): Promise<PaginatedResponse<AuditAlert>> {
    const params = new URLSearchParams();
    
    if (filter.severity?.length) params.append('severity', filter.severity.join(','));
    if (filter.type?.length) params.append('type', filter.type.join(','));
    if (filter.resolved !== undefined) params.append('resolved', filter.resolved.toString());
    if (filter.dateRange?.start) params.append('startTime', filter.dateRange.start.toISOString());
    if (filter.dateRange?.end) params.append('endTime', filter.dateRange.end.toISOString());
    params.append('page', page.toString());
    params.append('limit', limit.toString());

    const queryString = params.toString();
    return this.request<PaginatedResponse<AuditAlert>>(`/api/alerts${queryString ? `?${queryString}` : ''}`);
  }

  async getAllUnresolvedAlerts(): Promise<AuditAlert[]> {
    const allAlerts: AuditAlert[] = [];
    let page = 1;
    let hasMore = true;
    const limit = 100; // Fetch in larger batches

    while (hasMore) {
      const response = await this.getAlerts({ resolved: false }, page, limit);
      allAlerts.push(...response.items);
      hasMore = response.page < response.pages;
      page++;
    }

    return allAlerts;
  }

  async resolveAlert(alertId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(`/api/alerts/${alertId}/resolve`, {
      method: 'POST',
    });
  }

  // Validators
  async getValidators(): Promise<ValidatorData[]> {
    return this.request<ValidatorData[]>('/api/validators');
  }

  // Validator Health Monitoring
  async getValidatorHealthSummary(): Promise<ValidatorHealthSummary> {
    return this.request<ValidatorHealthSummary>('/api/validators/health');
  }

  // Reports
  async getReports(type?: string, limit?: number): Promise<AuditReport[]> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    return this.request<AuditReport[]>(`/api/reports${queryString ? `?${queryString}` : ''}`);
  }

  async generateReport(type: 'hourly' | 'daily' | 'weekly' | 'monthly'): Promise<{ 
    success: boolean; 
    reportId: string; 
  }> {
    return this.request<{ success: boolean; reportId: string }>('/api/reports/generate', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  // Reconciliation
  async runReconciliation(): Promise<ReconciliationResult> {
    return this.request<ReconciliationResult>('/api/reconciliation/run', {
      method: 'POST',
    });
  }

  async getReconciliationHistory(limit?: number): Promise<ReconciliationResult[]> {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    
    const queryString = params.toString();
    return this.request<ReconciliationResult[]>(`/api/reconciliation/history${queryString ? `?${queryString}` : ''}`);
  }

  // Test endpoints
  async testAlert(type: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>('/api/test/alert', {
      method: 'POST',
      body: JSON.stringify({ type }),
    });
  }

  // Volume data
  async getVolumeData(days: number = 7): Promise<TransactionVolumeData[]> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    
    const queryString = params.toString();
    return this.request<TransactionVolumeData[]>(`/api/volume${queryString ? `?${queryString}` : ''}`);
  }

  // Authentication methods
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    // Store token if login successful
    if (response.success && response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async logout(): Promise<{ success: boolean }> {
    try {
      const response = await this.request<{ success: boolean }>('/api/auth/logout', {
        method: 'POST',
      });
      
      // Clear token regardless of response
      this.setToken(null);
      
      return response;
    } catch (error) {
      // Clear token even if logout fails
      this.setToken(null);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User> {
    return this.request<User>('/api/auth/me');
  }

  async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
    return this.request<CreateUserResponse>('/api/auth/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateProfile(updateData: {
    username?: string;
    email?: string;
    currentPassword?: string;
    newPassword?: string;
  }): Promise<{ success: boolean; error?: string }> {
    return this.request<{ success: boolean; error?: string }>('/api/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!this.token;
  }

  // Get stored token
  getToken(): string | null {
    return this.token;
  }
}

export const apiClient = new ApiClient();
export default apiClient; 